import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiPost, apiJson } from '../api';
import { colors, theme } from '../styles';

export default function LoginAssistant({ account, onClose }) {
  const [screenshot, setScreenshot] = useState(null);
  const [status, setStatus] = useState('Connecting...');
  const [clickDots, setClickDots] = useState([]);
  const [inputStatus, setInputStatus] = useState(null);
  const [typingText, setTypingText] = useState('');
  const [loggedIn, setLoggedIn] = useState(false);
  const imgRef = useRef(null);

  // WebSocket for screenshots
  useEffect(() => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${window.location.host}/ws/live/${account.id}`);

    ws.onopen = () => setStatus('Watching browser...');
    ws.onclose = () => setStatus('Disconnected');

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === 'screenshot') {
        setScreenshot(`data:image/jpeg;base64,${msg.data}`);
      } else if (msg.type === 'log') {
        if (msg.action === 'auth_success' || msg.action === 'authenticated') {
          setLoggedIn(true);
          setStatus('Logged in!');
        }
      }
    };

    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 30000);

    return () => { clearInterval(ping); ws.close(); };
  }, [account.id]);

  // Send input via HTTP
  const sendInput = useCallback(async (msg) => {
    setInputStatus('...');
    try {
      await apiPost(`/accounts/${account.id}/input`, msg);
      setInputStatus('OK');
    } catch (e) {
      setInputStatus('Failed');
    }
    setTimeout(() => setInputStatus(null), 1500);
  }, [account.id]);

  const handleImageClick = useCallback((e) => {
    if (!imgRef.current) return;
    const img = imgRef.current;
    const rect = img.getBoundingClientRect();
    const scale = Math.min(rect.width / 1280, rect.height / 800);
    const renderedW = 1280 * scale;
    const renderedH = 800 * scale;
    const offsetX = (rect.width - renderedW) / 2;
    const offsetY = (rect.height - renderedH) / 2;
    const x = (e.clientX - rect.left - offsetX) / scale;
    const y = (e.clientY - rect.top - offsetY) / scale;
    if (x < 0 || y < 0 || x > 1280 || y > 800) return;

    const dotId = Date.now();
    setClickDots(prev => [...prev.slice(-3), { id: dotId, x: e.clientX - rect.left, y: e.clientY - rect.top }]);
    setTimeout(() => setClickDots(prev => prev.filter(d => d.id !== dotId)), 800);

    sendInput({ action: 'click', x: Math.round(x), y: Math.round(y) });
  }, [sendInput]);

  const handleTypeSend = () => {
    if (!typingText) return;
    sendInput({ action: 'type', text: typingText });
    setTypingText('');
  };

  const confirmLogin = async () => {
    await apiPost(`/accounts/${account.id}/release-control`);
    onClose(true);
  };

  const cancel = async () => {
    await apiPost(`/accounts/${account.id}/stop`);
    onClose(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: colors.bgCard, borderRadius: 12, width: '90vw', maxWidth: 1000,
        border: `1px solid ${colors.borderLight}`, overflow: 'hidden',
        display: 'flex', flexDirection: 'column', maxHeight: '90vh',
      }}>
        {/* Header */}
        <div style={{
          padding: '12px 20px', borderBottom: `1px solid ${colors.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h3 style={{ color: '#fff', margin: 0, fontSize: 16 }}>
              Login: @{account.username}
            </h3>
            <div style={{ fontSize: 12, color: colors.textDim, marginTop: 2 }}>
              {status}
              {inputStatus && <span style={{ color: colors.green, marginLeft: 8 }}>[{inputStatus}]</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {loggedIn ? (
              <button style={theme.btnSuccess} onClick={confirmLogin}>Continue - I'm Logged In</button>
            ) : (
              <button style={{ ...theme.btnSecondary, fontSize: 12 }} onClick={cancel}>Cancel</button>
            )}
          </div>
        </div>

        {/* Browser screenshot - clickable */}
        <div style={{
          flex: 1, minHeight: 400, background: colors.bgDark,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'crosshair', position: 'relative', overflow: 'hidden',
        }}>
          {screenshot ? (
            <>
              <img
                ref={imgRef}
                src={screenshot}
                alt="Browser"
                style={{
                  width: '100%', height: '100%', objectFit: 'contain',
                  userSelect: 'none', cursor: 'crosshair', WebkitUserDrag: 'none',
                }}
                onClick={handleImageClick}
                draggable={false}
                onDragStart={e => e.preventDefault()}
              />
              {clickDots.map(dot => (
                <div key={dot.id} style={{
                  position: 'absolute', left: dot.x - 10, top: dot.y - 10,
                  width: 20, height: 20, borderRadius: '50%',
                  background: colors.green + '66', border: `3px solid ${colors.green}`,
                  pointerEvents: 'none',
                }} />
              ))}
            </>
          ) : (
            <div style={{ color: colors.textDimmer }}>Loading browser...</div>
          )}
        </div>

        {/* Input bar */}
        <div style={{
          display: 'flex', gap: 8, padding: '10px 16px',
          borderTop: `1px solid ${colors.border}`, alignItems: 'center',
          background: colors.bgCard,
        }}>
          <div style={{ fontSize: 12, color: colors.textDim, whiteSpace: 'nowrap' }}>
            1. Click a field above &nbsp; 2. Type below &nbsp; 3. Click Send
          </div>
          <input
            style={{ ...theme.input, flex: 1 }}
            value={typingText}
            onChange={e => setTypingText(e.target.value)}
            placeholder="Type text to enter into focused field..."
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleTypeSend(); } }}
            autoFocus
          />
          <button style={{ ...theme.btnSmall, background: colors.accent, color: '#fff', fontWeight: 600, padding: '8px 16px' }}
            onClick={handleTypeSend}>Send</button>
          {['Tab', 'Enter', 'Escape', 'Backspace'].map(key => (
            <button key={key} style={{
              ...theme.btnSmall, background: colors.bgInput, color: colors.textMuted,
              border: `1px solid ${colors.borderLight}`, padding: '6px 8px', fontSize: 11,
            }} onClick={() => sendInput({ action: 'key', key })}>
              {key === 'Backspace' ? 'Bksp' : key === 'Escape' ? 'Esc' : key}
            </button>
          ))}
        </div>

        {/* Help text */}
        {!loggedIn && (
          <div style={{ padding: '8px 16px', background: colors.bgDark, fontSize: 12, color: colors.textDim, borderTop: `1px solid ${colors.border}` }}>
            Click on the username/email field in the browser image above, then type your credentials in the input bar and press Send.
            Use Tab to move between fields. Click the login button when ready. If you see a CAPTCHA, click directly on it.
            Once logged in, click "Continue - I'm Logged In".
          </div>
        )}
      </div>
    </div>
  );
}
